import React, { useState, useEffect, useRef } from 'react';
import { getEmployeeScratchOrders, getOrderById } from '../services/api';
import { formatDateTime, getStatusColor, handleApiError } from '../utils/helpers';
import EmployeeOrderDetailsView from './EmployeeOrderDetailsView';
import SuccessModal from '../components/common/SuccessModal';
import WholesalerName from '../components/common/WholesalerName';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const EmployeeScratchOrders = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [originalOrders, setOriginalOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [itemsPerPage] = useState(20);
    const [searchLoading, setSearchLoading] = useState(false);

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState({ orderId: '', action: '', status: '' });

    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    const navigate = useNavigate();
    const { accessToken } = useAuth();

    const fetchOrders = async () => {
        if (!accessToken) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const data = await getEmployeeScratchOrders();
            
            let pageOrders = [];
            if (Array.isArray(data)) {
                pageOrders = data;
            } else if (data && data.content && Array.isArray(data.content)) {
                pageOrders = data.content;
            } else if (data && Array.isArray(data.orders)) {
                pageOrders = data.orders;
            } else if (data && typeof data === 'object') {
                pageOrders = [data];
            } else {
                pageOrders = [];
            }

            const sortedOrders = pageOrders.sort((a, b) => {
                const idA = parseInt(a.id) || 0;
                const idB = parseInt(b.id) || 0;
                return idB - idA;
            });

            setOrders(sortedOrders);
            setOriginalOrders(sortedOrders);
            setFilteredOrders(sortedOrders);

            const totalEls = data?.totalElements || data?.total_elements || data?.total || pageOrders.length;
            setTotalElements(Number(totalEls));
            setTotalPages(Math.ceil(totalEls / itemsPerPage) || 1);
        } catch (err) {
            console.error('Error fetching employee scratch orders:', err);
            setOrders([]);
            setFilteredOrders([]);
            setOriginalOrders([]);
            if (handleApiError(err, navigate)) return;
            setError('Failed to fetch scratch orders: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accessToken) {
            fetchOrders();
        }
    }, [accessToken]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        searchTimeoutRef.current = setTimeout(() => {
            performSearch(value);
        }, 300);
    };

    const performSearch = async (query) => {
        if (!query || !query.trim()) {
            setFilteredOrders(originalOrders);
            setTotalElements(originalOrders.length);
            setSearchLoading(false);
            return;
        }

        setSearchLoading(true);
        try {
            // Try searching in the current list first
            const localMatch = originalOrders.filter(o => String(o.id).includes(query));
            if (localMatch.length > 0) {
                setFilteredOrders(localMatch);
                setTotalElements(localMatch.length);
            } else {
                // If not found locally, try fetching by ID
                const order = await getOrderById(query);
                if (abortControllerRef.current?.signal.aborted) return;
                if (order && order.id) {
                    setFilteredOrders([order]);
                    setTotalElements(1);
                } else {
                    setFilteredOrders([]);
                    setTotalElements(0);
                }
            }
        } catch (err) {
            if (abortControllerRef.current?.signal.aborted) return;
            setFilteredOrders([]);
            setTotalElements(0);
        }
        setSearchLoading(false);
    };

    const handleViewDetails = async (orderId) => {
        setDetailsError(null);
        setDetailsLoading(true);
        try {
            // Check if we already have full details in the list
            const localOrder = originalOrders.find(o => String(o.id) === String(orderId));
            if (localOrder && localOrder.scratchCardOrders) {
                setSelectedOrder(localOrder);
            } else {
                const details = await getOrderById(orderId);
                setSelectedOrder(details);
            }
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setDetailsError('Failed to fetch order details');
            setSelectedOrder(null);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleBackToList = () => {
        setSelectedOrder(null);
        setDetailsError(null);
    };

    const handleOrderUpdate = (orderId, newStatus, successData = null) => {
        if (successData && successData.refresh && successData.order) {
            setSelectedOrder(successData.order);
            // Also update in the list
            setFilteredOrders(prev => prev.map(o => o.id === orderId ? successData.order : o));
            setOriginalOrders(prev => prev.map(o => o.id === orderId ? successData.order : o));
            return;
        }

        // If order is delivered/completed, remove it from the list
        if (newStatus === 'DELIVERED' || newStatus === 'COMPLETED') {
            setFilteredOrders(prev => prev.filter(order => order.id !== orderId));
            setOriginalOrders(prev => prev.filter(order => order.id !== orderId));
            setTotalElements(prev => prev - 1);
            setSelectedOrder(null);
        }

        if (successData) {
            setSuccessModalData(successData);
            setShowSuccessModal(true);
        }
    };

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        setSuccessModalData({ orderId: '', action: '', status: '' });
    };

    if (error) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Scratch Orders Management</h1>
                <p className="text-gray-600">View and manage your scratch card orders.</p>
            </div>

            {!selectedOrder && (
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Search Orders</label>
                            <input
                                type="text"
                                placeholder="Search by Order ID..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {searchLoading && (
                                <div className="mt-2 text-sm text-gray-500">Searching...</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {selectedOrder ? (
                <div>
                    <button
                        className="mb-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
                        onClick={handleBackToList}
                    >
                        ← Back to List
                    </button>
                    {detailsLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : detailsError ? (
                        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">{detailsError}</div>
                    ) : (
                        <EmployeeOrderDetailsView order={selectedOrder} onOrderUpdate={handleOrderUpdate} />
                    )}
                </div>
            ) : (
                <>
                    <div className="text-sm text-gray-600 mb-4">
                        Showing {filteredOrders.length} orders
                    </div>

                    {loading ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                            <div className="flex justify-center items-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
                            <p className="text-gray-500 text-lg">No orders found</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredOrders.map((order) => (
                                            <tr key={order.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>{order.assignedDealer || order.dealerName || '-'}</div>
                                                    {order.wholesalerId && (
                                                        <WholesalerName wholesalerId={order.wholesalerId} />
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateTime(order.createdAt)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <button className="text-blue-600 hover:text-blue-900 mr-3" onClick={() => handleViewDetails(order.id)}>View Details</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={handleCloseSuccessModal}
                orderId={successModalData.orderId}
                action={successModalData.action}
                status={successModalData.status}
            />
        </div>
    );
};

export default EmployeeScratchOrders;
